import { Component, OnInit, OnDestroy, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ProjectFormPanelComponent } from './project-form-panel/project-form-panel.component';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ManagersService } from 'src/app/shared/services/managers/managers.service';
import { APP_CONST } from 'src/app/shared/constants/app-constants';
import { isSelfServiceManager } from './project-form-panel/project-form.util';
import * as _ from 'lodash';

@Component({
    selector: 'app-account-settings-project',
    templateUrl: './account-settings-project.component.html',
    styleUrls: ['./account-settings-project.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, ProjectFormPanelComponent, NgbTooltip]
})
export class AccountSettingsProjectComponent implements OnInit, OnDestroy {
  @ViewChild(ProjectFormPanelComponent) sidePanelComponent!: ProjectFormPanelComponent;
  
  accountsDetails: any;
  accountId!: string;
  data: any;
  projects: any[] = [];
  projectsList: any[] = [];
  isNoMoreClosedProject: boolean = false;
  searchValue: string = '';
  
  // Table configuration
  tableColumns: any[] = [];
  tableData: any[] = [];
  tableConfigs: any = {};

  isUserHasWriteAccess: boolean = false;
  isSelfServiceUser: boolean = false;
  private isRedirectingToNewProject: boolean = false;

  constructor(
    private AccountsService: AccountsService,
    private route: ActivatedRoute,
    private router: Router,
    private _toast: ToastService,
    private helpersService: HelpersService,
    private appStateService: AppStateService,
    private managersService: ManagersService
  ) {
    effect(() => {
      if (this.appStateService.initialized()) {
        this.isUserHasWriteAccess = this.isUserHasWriteAccessFn();
        this.isSelfServiceUser = isSelfServiceManager();
      }
    });
  }

  statusMap: any = this.helpersService.statusMap;

  ngOnInit() {
    this.isUserHasWriteAccess = this.isUserHasWriteAccessFn();
    this.isSelfServiceUser = isSelfServiceManager();
    this.route.queryParams.subscribe(params => {
      this.accountId = params['account_id'];
      this.getClosedProject();
    });
  }

  isUserHasWriteAccessFn(): boolean {
    const adminRulesDic = _.cloneDeep(this.helpersService.helpers['adminRulesDic'] || {});
    if (adminRulesDic.ACCOUNT_CREATION_NEW_PROJECT && !_.isEmpty(adminRulesDic.ACCOUNT_CREATION_NEW_PROJECT) && adminRulesDic.ACCOUNT_CREATION_NEW_PROJECT.write) {
      return true;
    }
    return false;
  }

  /**
   * Function - getClosedProject() 
   * @param -  {}
   * @description - Get all the closed projects of selected account
   */
  getClosedProject() {
    this.projects = [];
    this.projectsList = [];
    this.isNoMoreClosedProject = false;
    this.AccountsService.getBrandsList(this.accountId).subscribe(
      (response) => {
        if (response && response.data && response.data.brands && response.data.brands.length) {
          const brands = response.data.brands.map((brand: any) => {
            if (brand.project_info && brand.project_info.project_status) {
              brand.project_status_name = this.statusMap[brand.project_info.project_status.toString()];
            }
            return brand;
          });
          this.projects = brands;
          this.projectsList = brands;
        } else {
          this.isNoMoreClosedProject = true;
        }
      },
      (error) => {
        console.error('Error during account details API call:', error);
        this.isNoMoreClosedProject = true;
      }
    );
  }

  /**
   * Function - updateBrandDetails() 
   * @Description  This function will update the brand details
   * @param       {boolean}        value         boolean value which need to be update.
   * @param       {object}         id            Selected brand Id
   */
  updateBrandDetails(brand: any) {
    if (!this.isUserHasWriteAccess) {
      this._toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to update project visibility. Only read access is available.', 4000);
      return;
    }

    this.data = {};
    
    // Check if project status is not closed (3), then return
    if (!(brand.project_info && brand.project_info.project_status && brand.project_info.project_status == 3)) {
      return;
    }
    
    if (brand.show_on_dashboard == null || brand.show_on_dashboard == true) {
      this.data.show_on_dashboard = false;
    } else {
      this.data.show_on_dashboard = true;
    }

    this.AccountsService.updateBrandsConfiguration(brand._id, this.data).subscribe(
      (response) => {
        if (response && response.data.code == 0) {
          this._toast.showPopup('SUCCESS', 'Success', 'General settings updated successfully.', 5000);
          // Update the local projects array
          this.projects = this.projects.map((project: any) => {
            if (project._id == brand._id) {
              project.show_on_dashboard = this.data.show_on_dashboard;
            }
            return project;
          });
          this.syncProjectVisibilityCaches(brand._id, this.data.show_on_dashboard);
        } else {
          this._toast.showPopup('ERROR', 'Error', 'Something went wrong', 5000);
          // Revert the change in local array
          this.projects = this.projects.map((project: any) => {
            if (project._id == brand._id) {
              project.show_on_dashboard = brand.show_on_dashboard;
            }
            return project;
          });
        }
      },
      (error) => {
        console.error('Error while updating General settings:', error);
        this._toast.showPopup('ERROR', 'Error', 'Error while updating General settings.', 5000);
        // Revert the change in local array
        this.projects = this.projects.map((project: any) => {
          if (project._id == brand._id) {
            project.show_on_dashboard = brand.show_on_dashboard;
          }
          return project;
        });
      }
    );
  }

  /**
   * Function - syncProjectVisibilityCaches
   * Home, the header project switcher and the feed list all read `show_on_dashboard`
   * from the cached brand list rather than refetching, so the cache has to be updated
   * here or the project keeps showing until the next full page load.
   */
  private syncProjectVisibilityCaches(brandId: string, showOnDashboard: boolean) {
    const brandList = _.get(this.helpersService.helpers, 'brandListDetail', []);

    if (Array.isArray(brandList)) {
      const cachedBrand = brandList.find((item: any) => item && item._id == brandId);
      if (cachedBrand) {
        cachedBrand.show_on_dashboard = showOnDashboard;
        this.helpersService.helpers['brandListDetail'] = brandList;
        this.appStateService.updateBrandListdetailsForAccount(brandList);
        (window as any)['Helpers'] = this.helpersService.consoleData();
      }
    }

    if (showOnDashboard === false) {
      this.removeFromRecentlyVisited(brandId);
    }
  }

  /**
   * Function - removeFromRecentlyVisited
   * Drops a hidden project from the Recently Visited cache in local storage.
   */
  private removeFromRecentlyVisited(brandId: string) {
    const stored = localStorage.getItem('RVLIST');
    if (!stored) {
      return;
    }

    try {
      const recentVisit = JSON.parse(stored);
      if (!recentVisit || Array.isArray(recentVisit)) {
        return;
      }

      let hasChanges = false;
      Object.keys(recentVisit).forEach((key: string) => {
        const visitList = recentVisit[key];
        if (!Array.isArray(visitList)) {
          return;
        }
        const filtered = visitList.filter((entry: any) => entry?.project_id != brandId);
        if (filtered.length !== visitList.length) {
          recentVisit[key] = filtered;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        localStorage.setItem('RVLIST', JSON.stringify(recentVisit));
      }
    } catch (error) {
      // A malformed cache should never block the visibility update itself
      console.error('Error while updating recently visited projects cache:', error);
    }
  }

  /**
   * Function - searchProject() 
   * @param -  {}
   * @description - Search the project according to search input 
   */
  searchProject() {
    if (this.projectsList && this.projectsList.length > 0) {
      this.isNoMoreClosedProject = false;
      if (this.searchValue && this.searchValue.length > 0) {
        const searchTerm = this.searchValue.toUpperCase();
        const sortedProjects = this.projectsList.filter((project: any) => {
          const name = (project?.name || '').toUpperCase();
          const creator = this.getCreatorName(project).toUpperCase();
          return name.indexOf(searchTerm) >= 0 || creator.indexOf(searchTerm) >= 0;
        });
        if (sortedProjects && sortedProjects.length > 0) {
          this.projects = sortedProjects;
        } else {
          this.projects = [];
          this.isNoMoreClosedProject = true;
        }
      } else {
        this.projects = this.projectsList;
      }
    } else {
      this.isNoMoreClosedProject = true;
    }
  }

  getCreatorName(project: any): string {
    return project?.created_by?.user_name || 'NA';
  }

  getCreatedDate(project: any): string {
    if (!project?.created_date) {
      return 'NA';
    }
    const createdDate = new Date(project.created_date);
    if (Number.isNaN(createdDate.getTime())) {
      return 'NA';
    }
    return createdDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  /**
   * Function - getIsDisplayOnDashboard
   * @param brand - Brand object
   * @returns boolean - Whether to display on dashboard
   */
  getIsDisplayOnDashboard(brand: any): boolean {
    let isDisplay = false;
    const projectStatus = brand?.project_info?.project_status;
    
    if (projectStatus) {
      if ([1, 2, 3, 6].indexOf(projectStatus) != -1 && brand.show_on_dashboard != false) {
        isDisplay = true;
      } else {
        isDisplay = false;
      }
    }
    
    return isDisplay;
  }

  /**
   * Function - openNewProjectPanel
   * This method is used to open the create project side panel
   */
  openNewProjectPanel() {
    if (!this.isUserHasWriteAccess) {
      this._toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to create or clone projects. Only read access is available.', 4000);
      return;
    }
    if (this.sidePanelComponent) {
      this.searchValue = '';
      this.searchProject();
      this.sidePanelComponent.open();
    }
  }

  /**
   * Function - onProjectCreated
   * This method is called when a new project is created and when the panel is closed.
   * With a new project id the user is taken straight into that project, otherwise the
   * list is refreshed in place.
   */
  onProjectCreated(projectData?: any) {
    if (this.isRedirectingToNewProject) {
      return;
    }

    const newProjectId = projectData?.brand_id;
    if (newProjectId) {
      this.isRedirectingToNewProject = true;
      this.openNewProject(newProjectId);
      return;
    }

    // Refresh the project list
    this.projects = [];
    this.projectsList = [];
    this.tableData = [];
    this.getClosedProject();
  }

  /**
   * Function - openNewProject
   * Sets the newly created project as the current project and lands the user on its
   * conversation setup page. Every step falls through to the navigation so the user is
   * never stranded on the creation screen.
   */
  private openNewProject(projectId: string) {
    this.refreshBrandListCache(() => {
      this.managersService.setCurrentLoggedInManager(this.accountId, projectId).subscribe({
        next: () => this.navigateToNewProject(projectId),
        error: () => this.navigateToNewProject(projectId)
      });
    });
  }

  /**
   * Function - refreshBrandListCache
   * The cached brand list drives the project switcher and Home, and it does not yet
   * contain the project we are about to open, so it is refetched before navigating.
   */
  private refreshBrandListCache(done: () => void) {
    this.AccountsService.getBrandsList(this.accountId).subscribe({
      next: (response: any) => {
        const brands = response?.data?.brands;
        if (Array.isArray(brands)) {
          this.helpersService.helpers['brandListDetail'] = brands;
          this.appStateService.updateBrandListdetailsForAccount(brands);
          (window as any)['Helpers'] = this.helpersService.consoleData();
        }
        done();
      },
      error: () => done()
    });
  }

  private navigateToNewProject(projectId: string) {
    if (this.helpersService.helpers?.['managerDetails']?.['last_synced_details']) {
      delete this.helpersService.helpers['managerDetails']['last_synced_details'];
      (window as any)['Helpers'] = this.helpersService.consoleData();
    }
    this.router.navigate([APP_CONST.ROUTE_PROJECT_INTERACTION_SETTINGS], {
      queryParams: {
        account_id: this.accountId,
        project_id: projectId
      }
    });
  }

  /**
   * Function - isProjectClosed
   * Check if project status is Closed (status = 3)
   */
  isProjectClosed(brand: any): boolean {
    return !!(brand.project_info && brand.project_info.project_status && brand.project_info.project_status == 3);
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy() {
    // Cleanup if needed
  }

}
